import React, { useState, useContext, createContext } from "react";
import WorkoutModal from "../components/workouts/WorkoutModal";
import StudentModal from "../components/students/modals/StudentModal";

import FichaModel from "../components/workouts/FichaModal";
import PrescriptionExercicieModal from "../components/exercises/PrescriptionExercicieModal";
import utils from "../helpers/utils";
import { toast } from "react-toastify";
import workoutplans from "../services/workoutplans";
import AvaliacaoForm from "../components/avaliations/AvaliacaoForm";
import Objects from "../constants/Objects";
import {
  WorkoutPlan,
  Prescription,
  ExerciseInPlan,
  User,
  Avaliation,
  PrescriptionContextType,
  PrescriptionProviderProps,
  WorkoutPlanPayload,
} from "../types";

const PrescriptionContext = createContext<PrescriptionContextType>(
  {} as PrescriptionContextType,
);

const INIT_WORK: Partial<WorkoutPlan> = {
  name: "", // plan_name mapped to name? Interface has name.
  // objective: "", // WorkoutPlan interface has objective? Yes.
  start_date: new Date().toISOString(), // Interface uses string
  end_date: new Date().toISOString(),
  id: undefined,
  created_at: new Date().toISOString(),
  prescriptions: [],
};

const NEWEXERCICIE = {
  sets: "", // series?
  reps: "", // repetitions?
  mid: "",
  id: undefined,
  interval: "",
  charge: "", // load?
};

export const PrescriptionProvider = ({
  children,
}: PrescriptionProviderProps) => {
  const [workout, setWorkout] = useState<Partial<WorkoutPlan> | null>(null);
  const [ficha, setFicha] = useState<Prescription | null>(null);
  const [exercicie, setExercicie] = useState<Partial<ExerciseInPlan> | null>(
    null,
  );
  const [avaliacao, setAvaliacao] = useState<Partial<Avaliation> | null>(null);
  const [userModal, setUserModal] = useState<User | null>(null);

  const submitWorkout = async (form: Partial<WorkoutPlan>) => {
    const updatedForm = {
      ...workout,
      ...form,
      prescriptions: workout?.prescriptions,
    };

    // Normalizar os dados dos exercícios antes de enviar
    const normalizedForm = {
      ...updatedForm,
      prescriptions: updatedForm.prescriptions?.map((p) => ({
        ...p,
        exercises: p.exercises.map((e) => {
          const sets = Number(e.sets || e.series || 0);
          const reps = Number(e.reps || e.repetitions || 0);
          const interval = Number(e.interval || 0);

          return {
            id: Number(e.id) || 0,
            name: e.name || "",
            sets: sets,
            reps: reps,
            series: sets,
            repetitions: reps,
            load: String(e.load || e.charge || ""),
            interval: interval,
            professional: Number(e.professional || form.professional || 0),
          };
        }),
      })),
    };

    if (normalizedForm?.id) {
      const rep = await workoutplans.update_plan(
        String(normalizedForm.id),
        normalizedForm as unknown as WorkoutPlanPayload,
      );
      if (rep) {
        toast.success("Plano de Treino atualizado com sucesso!");
        reset();
      }
    } else {
      const rep = await workoutplans.create_plan({
        ...normalizedForm,
        user_id: userModal?.id,
      } as unknown as WorkoutPlanPayload);
      if (rep) {
        toast.success("Plano de Treino criado com sucesso!");
        reset();
      }
    }
  };

  const reset = () => {
    setWorkout(null);
    setFicha(null);
    setExercicie(null);
  };

  const newPrescription = () => {
    setWorkout(INIT_WORK);
  };

  const saveWorkout = (workouts: Partial<WorkoutPlan>) => {
    setWorkout({
      ...workout,
      ...workouts,
    });
  };

  function addFicha(ids = utils.generateId()) {
    const init: Prescription = {
      duration: "",
      end_date: "",
      exercises: [],
      id: ids + utils.generateId(),
      intensity: "",
      name: "",
      start_date: "",
      workout_type: "",
    };
    let fins = workout?.prescriptions ?? ([] as Prescription[]);

    fins.push(init);

    setWorkout({
      ...workout,
      prescriptions: fins,
    });
  }

  const setFichaId = (id: string) => {
    // prescriptions type check
    // In backend model, prescriptions is a list of objects.
    // In frontend types, it is Prescription[].
    // But here we are filtering by id? Prescription interface doesn't have ID.
    // The legacy code adds IDs to prescriptions (fichas).
    const prescs: Prescription[] = workout?.prescriptions || [];
    const fd = prescs.filter((e) => e.id === id);
    if (fd && fd.length > 0) {
      setFicha(fd[0]);
    }
  };

  const removeFichaId = (ids: string) => {
    const prescs: Prescription[] = workout?.prescriptions || [];
    const fd = prescs.filter((e) => e.id !== ids);
    setWorkout({
      ...workout,
      prescriptions: fd,
    });
  };

  const saveExercicie = (save: Partial<ExerciseInPlan>) => {
    const m_exercises = ficha?.exercises?.filter((e) => {
      if (save.mid && e.mid === save.mid) {
        return true;
      }
      return false;
    });

    if (m_exercises && m_exercises.length > 0) {
      const finalFicha = {
        ...ficha,
        exercises: ficha?.exercises.map((e) => {
          if (save.mid && e.mid === save.mid) {
            return save as ExerciseInPlan;
          }
          return e;
        }),
      } as Prescription;
      setFicha(finalFicha);
      updateWorkoutByFicha(finalFicha);
    } else {
      const final = [
        ...(ficha?.exercises || []),
        { mid: utils.generateId(save.mid), ...save } as ExerciseInPlan,
      ];
      const finalFicha = {
        ...ficha,
        exercises: final,
      } as Prescription;
      setFicha(finalFicha);
      updateWorkoutByFicha(finalFicha);
    }
    setExercicie(null);
  };

  const addExercicie = () => {
    if (!ficha) return;
    const finalExercicies = [
      ...ficha.exercises,
      { ...NEWEXERCICIE, mid: utils.generateId() } as unknown as ExerciseInPlan,
    ];

    const finalFicha = {
      ...ficha,
      exercises: finalExercicies,
    } as Prescription;

    setFicha(finalFicha);
    updateWorkoutByFicha(finalFicha);
  };

  const removeExercicie = (mid: string) => {
    if (!ficha) return;
    const finalExercicies = ficha.exercises.filter((e) => e.mid !== mid);
    const finalFicha = {
      ...ficha,
      exercises: finalExercicies,
    } as Prescription;
    setFicha(finalFicha);
    updateWorkoutByFicha(finalFicha);
  };

  const updateFicha = (newficha: Prescription) => {
    updateWorkoutByFicha(newficha);
  };

  const updateWorkoutByFicha = (finalFicha: Prescription) => {
    const prescs: Prescription[] = workout?.prescriptions || [];
    const prescriptionss = prescs.map((s) => {
      if (s.id === finalFicha.id) {
        return finalFicha;
      }
      return s;
    });

    setWorkout({
      ...workout,
      prescriptions: prescriptionss,
    });
  };

  const addAvaliation = (user_id: string, professional_id: string) => {
    setAvaliacao({ ...Objects.avalicao, professional_id, user_id });
  };

  const updateAvaliation = (
    avaliation: Partial<Avaliation>,
    id: number | undefined = undefined,
  ) => {
    setAvaliacao({ ...Objects.avalicao, ...avaliation, id });
  };

  return (
    <PrescriptionContext.Provider
      value={{
        userModal,
        workout,
        exercicie,
        INIT_WORK,
        addExercicie,
        addAvaliation,
        removeExercicie,
        setUserModal,
        setExercicie,
        setWorkout,
        removeFichaId,
        setFichaId,
        updateFicha,
        saveExercicie,
        setFicha,
        submitWorkout,
        addFicha,
        updateAvaliation,
        saveWorkout,
        newPrescription,
      }}
    >
      {children}

      <StudentModal
        isOpen={userModal !== null && !workout && !avaliacao}
        data={userModal}
        onBack={() => setUserModal(null)}
        buttonOff={true}
      />

      <WorkoutModal
        data={workout}
        isOpen={workout !== null && !ficha}
        onBack={() => setWorkout(null)}
        user_id={userModal?.id}
      />

      <FichaModel
        isOpen={ficha !== null && !exercicie}
        data={ficha}
        onBack={() => setFicha(null)}
      />

      <PrescriptionExercicieModal
        isOpen={exercicie !== null}
        data={exercicie}
        onBack={() => setExercicie(null)}
      />
      <AvaliacaoForm
        isOpen={avaliacao !== null}
        data={avaliacao}
        onBack={() => setAvaliacao(null)}
      />
    </PrescriptionContext.Provider>
  );
};

// Crie um hook personalizado para acessar o contexto de autenticação
export const usePrescription = () => useContext(PrescriptionContext);
